import { unstable_isUnrecognizedActionError } from 'next/navigation';
import posthog from 'posthog-js';
import { captureError } from '@/lib/sentry';
import { useAppVersionStore } from '@/lib/stores/app-version.store';
import { BUILD_SHA } from '@/lib/version';

/** `transport` marks a failure of the hop itself, already reported here — a
    site that reports its own domain failures must not report it twice. */
export type ActionFailure = { success: false; error: string; status?: undefined; transport: true };

export const ACTION_FAILED = 'Something went wrong — try again';
export const ACTION_OFFLINE = 'You seem to be offline — try again';

/**
 * The one way a client component calls a Server Action. The action's own catch
 * covers the API; this covers the hop TO the action, which can reject before any
 * of that runs — a bare await then hangs on its busy flag forever (WEBAPP-229).
 */
export async function callAction<T extends { success: boolean }>(
  run: () => Promise<T>
): Promise<T | ActionFailure> {
  try {
    return await run();
  } catch (thrown) {
    // `redirect()` inside the action is success with the router already moving:
    // never settle, so busy holds until the page is gone instead of flashing.
    if (isRedirect(thrown)) return leaving();
    // A deploy replaced the build under this tab; nothing it asks will ever be
    // answered again, so the reload VersionWatcher only offers is forced here,
    // past `isReloadUnsafe`: held work needs actions too, and they are all dead.
    if (unstable_isUnrecognizedActionError(thrown)) {
      useAppVersionStore.getState().markUpdateAvailable();
      posthog.capture('app_version_reloaded', { from: BUILD_SHA, trigger: 'stale_action' });
      window.location.reload();
      return leaving();
    }
    if (navigator.onLine === false) {
      return { success: false, error: ACTION_OFFLINE, transport: true };
    }
    captureError(thrown, { module: 'server-action', operation: 'call' });
    return { success: false, error: ACTION_FAILED, transport: true };
  }
}

function isRedirect(thrown: unknown): boolean {
  return !!(thrown as { digest?: string })?.digest?.startsWith('NEXT_REDIRECT');
}

function leaving(): Promise<never> {
  return new Promise<never>(() => {});
}
